import "./styles.css";
import { addDays, formatHumanDate, toISODate, weekday } from "./domain/date";
import { buildDayPlan } from "./domain/planning";
import type { CalorieEntry, ISODate, WorkoutSession } from "./domain/types";
import { allCaloriePlans, allPrograms, allUserData, caloriesForDate, getSettings, initialize, putCalorie, putWorkout, replaceFromBackup, saveSettings, workoutForDate } from "./storage/db";
import { ensureWorkout } from "./storage/workouts";
import { newSalt } from "./sync/crypto";
import { pullSync, pushSync } from "./sync/sync";

const app = document.querySelector<HTMLDivElement>("#app");
if (!app) throw new Error("App root not found");
const appRoot = app;

let selectedDate = toISODate(new Date());
let route: "today" | "history" | "progress" | "settings" = "today";
let syncStatus = "Сохранено";

void boot();

async function boot(): Promise<void> {
  await initialize();
  if ("serviceWorker" in navigator) await navigator.serviceWorker.register("/sw.js");
  window.addEventListener("online", () => {
    syncStatus = "Online";
    void render();
  });
  window.addEventListener("offline", () => {
    syncStatus = "Offline";
    void render();
  });
  await render();
}

async function render(): Promise<void> {
  const [programs, caloriePlans, calories, settings] = await Promise.all([allPrograms(), allCaloriePlans(), caloriesForDate(selectedDate), getSettings()]);
  const plan = buildDayPlan(programs, caloriePlans, selectedDate);
  const total = calories.filter((entry) => !entry.deletedAt).reduce((sum, entry) => sum + entry.kcal, 0);
  appRoot.innerHTML = `
    <main class="shell">
      <header class="topbar">
        <div>
          <p class="eyebrow">${weekday(selectedDate)}</p>
          <h1>${formatHumanDate(selectedDate)}</h1>
          <p class="muted">${plan.phase.name} · день ${plan.programDay}</p>
        </div>
        <span class="status">${navigator.onLine ? syncStatus : "Offline"}</span>
      </header>
      <nav class="tabs">
        ${tab("today", "Сегодня")}
        ${tab("history", "История")}
        ${tab("progress", "Прогресс")}
        ${tab("settings", "Настройки")}
      </nav>
      ${route === "today" ? await todayView(plan, calories, total) : ""}
      ${route === "history" ? await historyView() : ""}
      ${route === "progress" ? await progressView() : ""}
      ${route === "settings" ? settingsView(settings.cloudEndpoint, settings.encryptionSalt) : ""}
    </main>
  `;
  bindCommon();
}

function tab(target: typeof route, label: string): string {
  return `<button class="tab ${route === target ? "active" : ""}" data-route="${target}">${label}</button>`;
}

async function todayView(plan: ReturnType<typeof buildDayPlan>, calories: CalorieEntry[], total: number): Promise<string> {
  const workout = await ensureWorkout(selectedDate, await allPrograms());
  const over = total - plan.calorieTarget;
  const remaining = plan.calorieTarget - total;
  const percent = Math.min(120, Math.round((total / plan.calorieTarget) * 100));
  return `
    <section class="date-nav">
      <button data-date="${addDays(selectedDate, -1)}">←</button>
      <button data-date="${toISODate(new Date())}">Сегодня</button>
      <button data-date="${addDays(selectedDate, 1)}">→</button>
      <input type="date" value="${selectedDate}" data-calendar />
    </section>
    <section class="calories">
      <div class="row">
        <div>
          <p class="eyebrow">Калории</p>
          <strong>${total} / ${plan.calorieTarget} kcal</strong>
          <p class="${over > 0 ? "danger" : "muted"}">${over > 0 ? `+${over} kcal` : `Осталось ${remaining} kcal`}</p>
        </div>
        <button class="primary" data-add-calories>Добавить</button>
      </div>
      <div class="meter"><span style="width:${percent}%"></span></div>
      <div class="quick">${[50, 100, 200, 500].map((n) => `<button data-quick="${n}">+${n}</button>`).join("")}</div>
    </section>
    ${calorieHistory(calories)}
    <section class="panel">
      <h2>Тренировка</h2>
      <p class="muted">${plan.blocks.map((block) => block.title).join(" · ")}</p>
      ${workoutView(workout)}
    </section>
    <dialog id="calorieDialog">
      <form method="dialog" class="dialog-form">
        <h2>Добавить калории</h2>
        <label>Ккал <input inputmode="numeric" name="kcal" required autofocus /></label>
        <label>Название <input name="title" placeholder="еда или комментарий" /></label>
        <div class="macro-grid">
          <label>Белки <input inputmode="decimal" name="protein" /></label>
          <label>Жиры <input inputmode="decimal" name="fat" /></label>
          <label>Углеводы <input inputmode="decimal" name="carbs" /></label>
        </div>
        <menu><button value="cancel">Отмена</button><button class="primary" value="save">Сохранить</button></menu>
      </form>
    </dialog>
  `;
}

function calorieHistory(entries: CalorieEntry[]): string {
  const visible = entries.filter((entry) => !entry.deletedAt).sort((a, b) => b.occurredAt.localeCompare(a.occurredAt));
  return `<section class="panel"><h2>Еда за день</h2>${visible.length === 0 ? `<p class="muted">Записей пока нет.</p>` : visible.map((entry) => `
    <article class="entry">
      <div><strong>${entry.kcal} kcal</strong><p>${entry.title || "Без названия"}</p></div>
      <div class="entry-actions">
        <button data-repeat="${entry.id}">Повторить</button>
        <button data-edit-calorie="${entry.id}">Изм.</button>
        <button data-delete-calorie="${entry.id}">Удалить</button>
      </div>
    </article>`).join("")}</section>`;
}

function workoutView(workout: WorkoutSession): string {
  return workout.exerciseSessions.map((exercise, exerciseIndex) => `
    <article class="exercise ${exercise.tracking === "block" ? "block-exercise" : ""}">
      <div class="exercise-head">
        <h3>${exercise.name}</h3>
        <p class="muted">План: ${exercise.target}</p>
        ${exercise.notes ? `<p class="notes">${exercise.notes}</p>` : ""}
      </div>
      ${exercise.tracking === "block" ? `
        <button class="block-toggle ${exercise.sets[0]?.done ? "done" : ""}" data-block-done="${exerciseIndex}">
          ${exercise.sets[0]?.done ? "Готово" : "Отметить блок"}
        </button>` : exercise.sets.map((set, setIndex) => `
        <div class="set-row">
          <span>Подход ${set.setNumber}</span>
          ${set.plannedSeconds !== undefined ? `
            <label><input data-set-seconds="${exerciseIndex}:${setIndex}" inputmode="numeric" value="${set.seconds ?? ""}" aria-label="Секунды" /><small>сек</small></label>
          ` : `
            <label><input data-set-reps="${exerciseIndex}:${setIndex}" inputmode="numeric" value="${set.reps ?? ""}" aria-label="Повторы" /><small>повт.</small></label>
          `}
          <button class="${set.done ? "done" : ""}" data-set-done="${exerciseIndex}:${setIndex}">✓</button>
        </div>`).join("")}
    </article>`).join("");
}

async function historyView(): Promise<string> {
  const start = addDays(selectedDate, -14);
  const days = Array.from({ length: 35 }, (_, i) => addDays(start, i));
  const cells = await Promise.all(days.map(async (date) => {
    const [programs, caloriePlans, calories, workout] = await Promise.all([allPrograms(), allCaloriePlans(), caloriesForDate(date), workoutForDate(date)]);
    const plan = buildDayPlan(programs, caloriePlans, date);
    const total = calories.filter((entry) => !entry.deletedAt).reduce((sum, entry) => sum + entry.kcal, 0);
    const done = workout?.exerciseSessions.some((exercise) => exercise.sets.some((set) => set.done));
    return `<button class="day-cell ${date === selectedDate ? "active" : ""}" data-date="${date}">
      <span>${date.slice(8)}</span><small>${done ? "✓" : ""}${total > plan.calorieTarget ? " +" : total ? " kcal" : ""}</small>
    </button>`;
  }));
  return `<section class="panel"><h2>Календарь</h2><div class="calendar">${cells.join("")}</div></section>`;
}

async function progressView(): Promise<string> {
  const data = await allUserData();
  const completed = data.workouts.flatMap((workout) => workout.exerciseSessions.flatMap((exercise) => exercise.sets.filter((set) => set.done).map((set) => ({ date: workout.date, name: exercise.name, reps: set.reps ?? 0 }))));
  const names = [...new Set(completed.map((item) => item.name))].slice(0, 12);
  const avgCalories = Math.round(data.calories.filter((entry) => !entry.deletedAt).reduce((sum, entry) => sum + entry.kcal, 0) / Math.max(1, new Set(data.calories.map((entry) => entry.date)).size));
  return `<section class="panel"><h2>Прогресс</h2><p class="muted">Выполнено подходов: ${completed.length} · средние калории: ${avgCalories}</p>
    ${names.map((name) => {
      const points = completed.filter((item) => item.name === name);
      const best = Math.max(...points.map((item) => item.reps), 0);
      return `<article class="entry"><div><strong>${name}</strong><p>Лучший результат: ${best || "нет данных"} повторов</p></div><small>${points.length} подходов</small></article>`;
    }).join("") || `<p class="muted">Заполни первую тренировку, и здесь появится история упражнений.</p>`}
  </section>`;
}

function settingsView(endpoint: string, salt?: string): string {
  return `<section class="panel">
    <h2>Настройки</h2>
    <label>Worker endpoint <input data-setting="cloudEndpoint" value="${endpoint}" placeholder="https://worker.example.workers.dev" /></label>
    <label>Access token <input data-setting="accessToken" type="password" placeholder="секрет из Worker" /></label>
    <button data-create-salt>${salt ? "Обновить salt" : "Создать salt шифрования"}</button>
    <button data-export>Экспорт backup</button>
    <label class="file">Импорт backup <input type="file" accept="application/json" data-import /></label>
    <button data-sync>Синхронизировать</button>
    <button data-pull>Получить из облака</button>
    <button data-reset-cache>Сбросить локальный кэш</button>
    <p class="muted">Пароль шифрования вводится только при синхронизации и не сохраняется в коде.</p>
  </section>`;
}

function bindCommon(): void {
  document.querySelectorAll<HTMLButtonElement>("[data-route]").forEach((button) => button.addEventListener("click", () => {
    route = button.dataset.route as typeof route;
    void render();
  }));
  document.querySelectorAll<HTMLElement>("[data-date]").forEach((item) => item.addEventListener("click", () => {
    selectedDate = item.dataset.date as ISODate;
    route = "today";
    void render();
  }));
  document.querySelector<HTMLInputElement>("[data-calendar]")?.addEventListener("change", (event) => {
    selectedDate = (event.target as HTMLInputElement).value as ISODate;
    void render();
  });
  bindCalories();
  bindWorkout();
  bindSettings();
}

function bindCalories(): void {
  const dialog = document.querySelector<HTMLDialogElement>("#calorieDialog");
  document.querySelector("[data-add-calories]")?.addEventListener("click", () => dialog?.showModal());
  dialog?.addEventListener("close", async () => {
    if (dialog.returnValue !== "save") return;
    const form = dialog.querySelector("form")!;
    const values = new FormData(form);
    await addCalorie(Number(values.get("kcal")), String(values.get("title") || ""), Number(values.get("protein") || 0), Number(values.get("fat") || 0), Number(values.get("carbs") || 0));
    form.reset();
  });
  document.querySelectorAll<HTMLButtonElement>("[data-quick]").forEach((button) => button.addEventListener("click", () => addCalorie(Number(button.dataset.quick), `Быстро +${button.dataset.quick}`)));
  document.querySelectorAll<HTMLButtonElement>("[data-delete-calorie]").forEach((button) => button.addEventListener("click", async () => {
    const entries = await caloriesForDate(selectedDate);
    const entry = entries.find((item) => item.id === button.dataset.deleteCalorie);
    if (entry) await putCalorie({ ...entry, deletedAt: new Date().toISOString() });
    await render();
  }));
  document.querySelectorAll<HTMLButtonElement>("[data-edit-calorie]").forEach((button) => button.addEventListener("click", async () => {
    const entries = await caloriesForDate(selectedDate);
    const entry = entries.find((item) => item.id === button.dataset.editCalorie);
    if (!entry) return;
    const next = Number(window.prompt("Новое значение kcal", String(entry.kcal)) || entry.kcal);
    if (!Number.isFinite(next) || next <= 0) return;
    await putCalorie({ ...entry, kcal: next });
    await render();
  }));
  document.querySelectorAll<HTMLButtonElement>("[data-repeat]").forEach((button) => button.addEventListener("click", async () => {
    const entries = await caloriesForDate(selectedDate);
    const entry = entries.find((item) => item.id === button.dataset.repeat);
    if (entry) await addCalorie(entry.kcal, entry.title ?? "", entry.protein, entry.fat, entry.carbs);
  }));
}

async function addCalorie(kcal: number, title = "", protein?: number, fat?: number, carbs?: number): Promise<void> {
  if (!Number.isFinite(kcal) || kcal <= 0) return;
  const now = new Date().toISOString();
  await putCalorie({ id: crypto.randomUUID(), createdAt: now, updatedAt: now, revision: 1, date: selectedDate, kcal, title, protein, fat, carbs, occurredAt: now });
  syncStatus = "Сохранено";
  await render();
}

function bindWorkout(): void {
  document.querySelectorAll<HTMLInputElement>("[data-set-reps]").forEach((input) => input.addEventListener("change", async () => {
    const workout = await ensureWorkout(selectedDate, await allPrograms());
    const [exerciseIndex, setIndex] = input.dataset.setReps!.split(":").map(Number);
    workout.exerciseSessions[exerciseIndex].sets[setIndex].reps = Number(input.value || 0);
    await putWorkout(workout);
    syncStatus = "Сохранено";
  }));
  document.querySelectorAll<HTMLInputElement>("[data-set-seconds]").forEach((input) => input.addEventListener("change", async () => {
    const workout = await ensureWorkout(selectedDate, await allPrograms());
    const [exerciseIndex, setIndex] = input.dataset.setSeconds!.split(":").map(Number);
    workout.exerciseSessions[exerciseIndex].sets[setIndex].seconds = Number(input.value || 0);
    await putWorkout(workout);
    syncStatus = "Сохранено";
  }));
  document.querySelectorAll<HTMLButtonElement>("[data-block-done]").forEach((button) => button.addEventListener("click", async () => {
    const workout = await ensureWorkout(selectedDate, await allPrograms());
    const exerciseIndex = Number(button.dataset.blockDone);
    const set = workout.exerciseSessions[exerciseIndex].sets[0];
    set.done = !set.done;
    if (workout.exerciseSessions.every((exercise) => exercise.sets.every((item) => item.done))) workout.completedAt = new Date().toISOString();
    await putWorkout(workout);
    await render();
  }));
  document.querySelectorAll<HTMLButtonElement>("[data-set-done]").forEach((button) => button.addEventListener("click", async () => {
    const workout = await ensureWorkout(selectedDate, await allPrograms());
    const [exerciseIndex, setIndex] = button.dataset.setDone!.split(":").map(Number);
    const set = workout.exerciseSessions[exerciseIndex].sets[setIndex];
    set.done = !set.done;
    if (workout.exerciseSessions.every((exercise) => exercise.sets.every((item) => item.done))) workout.completedAt = new Date().toISOString();
    await putWorkout(workout);
    await render();
  }));
}

function bindSettings(): void {
  document.querySelectorAll<HTMLInputElement>("[data-setting]").forEach((input) => input.addEventListener("change", async () => {
    const settings = await getSettings();
    await saveSettings({ ...settings, [input.dataset.setting!]: input.value });
  }));
  document.querySelector("[data-create-salt]")?.addEventListener("click", async () => {
    const settings = await getSettings();
    await saveSettings({ ...settings, encryptionSalt: newSalt() });
    await render();
  });
  document.querySelector("[data-export]")?.addEventListener("click", async () => {
    const payload = JSON.stringify(await allUserData(), null, 2);
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([payload], { type: "application/json" }));
    a.download = `training-backup-${selectedDate}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  });
  document.querySelector<HTMLInputElement>("[data-import]")?.addEventListener("change", async (event) => {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file || !confirm("Импорт заменит локальные данные. Продолжить?")) return;
    await replaceFromBackup(JSON.parse(await file.text()));
    await render();
  });
  document.querySelector("[data-sync]")?.addEventListener("click", async () => {
    const passphrase = window.prompt("Пароль шифрования") ?? "";
    if (!passphrase) return;
    syncStatus = "Синхронизация...";
    await render();
    try {
      syncStatus = await pushSync(passphrase);
    } catch (error) {
      syncStatus = error instanceof Error ? error.message : "Ошибка синхронизации";
    }
    await render();
  });
  document.querySelector("[data-pull]")?.addEventListener("click", async () => {
    if (!confirm("Заменить локальные данные облачной копией?")) return;
    const passphrase = window.prompt("Пароль шифрования") ?? "";
    if (!passphrase) return;
    try {
      syncStatus = await pullSync(passphrase);
    } catch (error) {
      syncStatus = error instanceof Error ? error.message : "Ошибка загрузки";
    }
    await render();
  });
  document.querySelector("[data-reset-cache]")?.addEventListener("click", () => {
    if (!confirm("Удалить локальный IndexedDB кэш? Перед этим сделай экспорт или синхронизацию.")) return;
    const request = indexedDB.deleteDatabase("training-calorie-app");
    request.onsuccess = () => location.reload();
  });
}
